import React, { useEffect, useState } from "react";
import { Card } from "antd";
import styled from "styled-components";
import _ from "lodash";

import { renderReactRowsJson } from "../../utils/renderReactFromJson";
import { PRIMARY_600 } from "../../../../../constant/colors";
import { useTranslation } from "react-i18next";

const StyledLink = styled.div`
  color: ${PRIMARY_600};
  cursor: pointer;
`;

const CardTitle = styled.span`
  font-weight: 600;
`;

const updateConditionsWithIteration = (conditions, iterationName) => {
  if (!conditions) return {};
  const clonedConditions = structuredClone(conditions);

  Object.values(clonedConditions).forEach((value) => {
    value.rules.forEach((rule) => {
      rule.relatedFieldName = rule.relatedFieldName.replace("{{fieldIterationNames}}", iterationName);
    });
  });

  return clonedConditions;
};

const updateRelatedFieldsMap = (relatedFieldsValuesMap, iterationName) => {
  if (!relatedFieldsValuesMap) return {};
  const clonedMap = structuredClone(relatedFieldsValuesMap);

  clonedMap.forEach((value) => {
    value[0][1] = value[0][1].replace("{{fieldIterationNames}}", iterationName);
  });

  return clonedMap;
};

const RemoveButton = ({ onClick }) => {
  const { t } = useTranslation();
  return <StyledLink onClick={onClick}>{t("remove")}</StyledLink>;
};

// Determine initial visible sections based on existing values
const getInitialVisibleCount = (form, section, fieldIterationNames) => {
  const allFields = form.getFieldsValue(true);
  const sectionValues = allFields?.[section] || {};

  // Count how many sections have at least one non-empty value excluding phoneType which has a default value
  let count = fieldIterationNames.filter((iteration) => {
    const camelCaseIterationName = _.camelCase(iteration);
    const sectionData = sectionValues?.[camelCaseIterationName];

    if (!sectionData) return false;

    // Check if any field (excluding phoneType) is not empty
    return Object.entries(sectionData).some(([key, value]) => {
      // Skip phoneType
      if (key === "phoneType") return false;

      // Check if value exists and is not empty
      return !!value;
    });
  }).length;

  return count > 0 ? count : 1; // Ensure at least one section is visible
};

export const FieldMultiplier = ({ name, label, fieldIterationNames, fieldIterationLabels = [], subFields, form }) => {
  const { t } = useTranslation();
  const section = name[0]; // First element of name array is the section name
  const [visibleCount, setVisibleCount] = useState(getInitialVisibleCount(form, section, fieldIterationNames));
  const isFormDisabled = form.getFieldValue("form_disabled");

  useEffect(() => {
    form.validateFields();
  }, [visibleCount]);

  const handleRemove = () => {
    if (visibleCount > 1) {
      setVisibleCount((prev) => prev - 1);
      const allFields = form.getFieldsValue(true);

      const currentSection = _.camelCase(fieldIterationNames[visibleCount - 1]);
      if (allFields?.[section]?.[currentSection]) {
        delete allFields?.[section]?.[currentSection];
      }
    }
  };

  const handleAdd = () => {
    if (visibleCount < fieldIterationNames.length) {
      setVisibleCount((prev) => prev + 1);
    }
  };

  return (
    <>
      {fieldIterationNames.map((iterationName, index) => {
        const camelCaseIterationName = _.camelCase(iterationName);
        const sectionKey = `${section}-${iterationName}`;
        const isVisible = index < visibleCount;

        return (
          <div key={sectionKey} style={{ display: isVisible ? "block" : "none", marginBottom: 16 }}>
            <Card
              size="small"
              title={<CardTitle>{fieldIterationLabels[index] || iterationName}</CardTitle>}
              extra={
                !isFormDisabled && index > 0 && index === visibleCount - 1 ? (
                  <RemoveButton onClick={handleRemove} />
                ) : null
              }
            >
              {subFields &&
                renderReactRowsJson({
                  section: { sectionName: section },
                  rows: subFields.rows.map((row) => ({
                    ...row,
                    fields: row.fields.map((field) => ({
                      ...field,
                      required: isVisible ? field.required : false,
                      subSectionName: camelCaseIterationName,
                      conditions: updateConditionsWithIteration(field.conditions, camelCaseIterationName),
                      relatedFieldsValuesMap: updateRelatedFieldsMap(
                        field.dedicatedFieldProps?.relatedFieldsValuesMap,
                        camelCaseIterationName
                      )
                    }))
                  })),
                  form
                })}
            </Card>
          </div>
        );
      })}

      {!isFormDisabled && visibleCount < fieldIterationNames.length && (
        <StyledLink onClick={handleAdd} style={{ marginBottom: 8 }}>
          {`+ ${t("add_another")} ${label}`}
        </StyledLink>
      )}
    </>
  );
};
